import Loader from 'components/_UI/Loader';
import DynamicProfileBackground from 'components/commons/DynamicProfileBackground';
import date from 'helpers/date';
import types from 'helpers/system/types';
import React, { useEffect, useRef, useState } from 'react'
import { Calendar, ChevronRight, Edit2, MapPin, MoreVertical, Plus, Send, ShoppingBag, Ticket, Trash2 } from 'lucide-react';
import styled from 'styled-components'
import { FullyCentered } from 'styled/alignment/Center';
import colors from 'theme/colors';
import eventTypes from 'theme/eventTypes';
import fonts from 'theme/fonts';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';


export default function EventItem({
    data,
    expired,
    draft,
    isLoading,
    onDropdownAction,
    dropDownVisible,
    onEventClick,
    onMoreClick,

}) {

    const [toogleDropdown, setToogleDropdown] = useState(null);
    const dropdownRef = useRef(null);
    const moreButtonRef = useRef(null);

    useEffect(() => {
        if (!toogleDropdown) return;

        function handleClickOutside(e) {
            if (
                dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                moreButtonRef.current && !moreButtonRef.current.contains(e.target)
            ) {
                setToogleDropdown(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [toogleDropdown]);

    function renderActions() {
        setToogleDropdown(!toogleDropdown)
        onMoreClick()
    }

    const ctaLabel = expired ? 'Ver detalhes' : 'Gerenciar evento';

    return (
        // O card inteiro continua clicável no mouse, mas quem carrega o papel de
        // controle é o botão do rodapé: o clique dele borbulha até aqui (inclusive
        // quando acionado por Enter/Espaço), então teclado e mouse compartilham o
        // mesmo handler sem aninhar elementos interativos dentro de um button.
        <CardContainer onClick={(e) => onEventClick(e)} aria-label={data?.name}>
            <ImageWrapper>
                <DynamicProfileBackground
                    img={data?.images}
                    word={data?.name}
                    size={'160px'}
                    event
                />

                <ClassificationBadge type={data?.classification?.name} colorType={!data.images?.length}>
                    {types.getBySlug(data?.classification?.name).label}
                </ClassificationBadge>

                <MoreButton
                    type='button'
                    aria-label={`Mais ações para ${data?.name}`}
                    aria-haspopup='menu'
                    aria-expanded={!!(toogleDropdown && dropDownVisible)}
                    ref={moreButtonRef}
                    onClick={(e) => { e.stopPropagation(); renderActions(); }}
                >
                    <MoreVertical size={16} color={colors.neutralLight} />
                </MoreButton>
            </ImageWrapper>

            <CardBody>
                <EventName>{data?.name}</EventName>

                <InfoRow>
                    <Calendar size={14} color={colors.night} />
                    <InfoText>
                        {`${date.format(data?.dates[0]?.date)} - ${data?.dates[0]?.start.split(':')[0]}:${data?.dates[0]?.start.split(':')[1]}`}
                    </InfoText>
                </InfoRow>

                <InfoRow>
                    <MapPin size={14} color={colors.night} />
                    {!data?.address ? (
                        <InfoText>Local a definir</InfoText>
                    ) : (
                        <InfoText>
                            {data?.address?.street?.name && `${data?.address?.street?.name},`} {data?.address?.street?.district && `Bairro ${data?.address?.street?.district}`}
                            {data?.address?.number && `, nº ${data?.address?.number}`}
                            {data?.address?.complement && ` - ${data?.address?.complement}`}
                        </InfoText>
                    )}
                </InfoRow>
            </CardBody>

            <CardFooter>
                <CtaButton type='button' aria-label={`${ctaLabel}: ${data?.name}`}>
                    {ctaLabel}
                    <ChevronRight size={15} strokeWidth={2.5} />
                </CtaButton>
            </CardFooter>

            {
                (toogleDropdown && dropDownVisible) && (
                    <DropDownActions role='menu' ref={dropdownRef} onClick={(e) => e.stopPropagation()}>
                        {
                            isLoading ? (
                                <FullyCentered style={{ padding: '20px 0' }}>
                                    <Loader />
                                </FullyCentered>
                            ) : (
                                <>
                                    {draft && (
                                        <ActionButton type='button' role='menuitem' onClick={() => onDropdownAction('publish')}>
                                            <Send size={14} color={colors.successDark} />
                                            <ActionLabel color={colors.successDark}>Publicar</ActionLabel>
                                        </ActionButton>
                                    )}

                                    {!expired && (
                                        <>
                                            <ActionButton type='button' role='menuitem' onClick={() => onDropdownAction('edit')}>
                                                <Edit2 size={14} color={colors.primary} />
                                                <ActionLabel color={colors.primary}>Editar evento</ActionLabel>
                                            </ActionButton>

                                            <ActionButton type='button' role='menuitem' onClick={() => onDropdownAction('create-ticket')}>
                                                <Ticket size={14} color={colors.primary} />
                                                <ActionLabel color={colors.primary}>Editar ingressos</ActionLabel>
                                            </ActionButton>

                                            <ActionButton type='button' role='menuitem' onClick={() => onDropdownAction('create-product')}>
                                                <ShoppingBag size={14} color={colors.primary} />
                                                <ActionLabel color={colors.primary}>
                                                    {data?.has_products ? 'Editar produtos' : 'Adicionar produtos'}
                                                </ActionLabel>
                                            </ActionButton>

                                            <ActionButton type='button' role='menuitem' onClick={() => onDropdownAction('additional-fields')}>
                                                <Plus size={14} color={colors.primary} />
                                                <ActionLabel color={colors.primary}>Campos adicionais</ActionLabel>
                                            </ActionButton>
                                        </>
                                    )}

                                    <ActionButton type='button' role='menuitem' onClick={() => onDropdownAction('delete')}>
                                        <Trash2 size={14} color={colors.danger} />
                                        <ActionLabel color={colors.danger}>Excluir</ActionLabel>
                                    </ActionButton>
                                </>
                            )
                        }
                    </DropDownActions>
                )
            }
        </CardContainer>
    )
}

const CardContainer = styled.article`
    width: 100%;
    border-radius: 12px;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.night}26;
    overflow: hidden;
    position: relative;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    /* Elevação já em repouso: o card precisa ler como objeto sobre a superfície,
       não como bloco impresso nela. */
    box-shadow: 0 1px 2px ${colors.nightDark}0F, 0 1px 3px ${colors.nightDark}0A;
    transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-3px);
        border-color: ${panelAccentAlpha(colors.secondaryPurple, '66')};
        box-shadow: 0 10px 24px ${colors.nightDark}1F;
    }

    &:hover img {
        transform: scale(1.05);
    }

    &:active {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px ${colors.nightDark}1A;
    }

    /* O foco vive no botão do rodapé; o anel é desenhado no card inteiro para o
       usuário de teclado ver qual evento está prestes a abrir. */
    &:focus-within {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: 2px;
        border-color: ${panelAccentAlpha(colors.secondaryPurple, '66')};
    }

    @media (prefers-reduced-motion: reduce) {
        transition: border-color 0.2s ease, box-shadow 0.2s ease;

        &:hover,
        &:active {
            transform: none;
        }

        &:hover img {
            transform: none;
        }
    }
`;

const ImageWrapper = styled.div`
    width: 100%;
    height: 160px;
    position: relative;
    overflow: hidden;

    & img {
        transition: transform 0.35s ease;
    }
`;

const ClassificationBadge = styled.div`
    position: absolute;
    top: 12px;
    left: 12px;
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 50px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    letter-spacing: 0.2px;
    white-space: nowrap;
    backdrop-filter: blur(8px);
    color: ${colors.neutralLight};

    ${({ type }) => type === eventTypes.free.slug && `
        background: ${eventTypes.free.color}E6;
    `}

    ${({ type }) => type === eventTypes.paid.slug && `
        background: ${eventTypes.paid.color}E6;
    `}

    ${({ colorType, type }) => colorType && type === eventTypes.paid.slug && `
        background: ${colors.neutralLight}E6;
        color: ${eventTypes.paid.color};
    `}

    ${({ colorType, type }) => colorType && type === eventTypes.free.slug && `
        background: ${colors.neutralLight}E6;
        color: ${eventTypes.free.color};
    `}
`;

const MoreButton = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 8px;
    background: ${colors.nightDark}44;
    backdrop-filter: blur(4px);
    cursor: pointer;
    transition: background 0.15s ease;
    padding: 0;

    &:hover {
        background: ${colors.nightDark}66;
    }

    &:focus-visible {
        outline: 2px solid ${colors.neutralLight};
        outline-offset: 2px;
    }
`;

const CardBody = styled.div`
    padding: 16px 16px 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
`;

const EventName = styled.p`
    font-size: ${fonts.sizes.default};
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
    padding: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.4;
    transition: color 0.2s ease;

    ${CardContainer}:hover &,
    ${CardContainer}:focus-within & {
        color: ${panelAccent(colors.secondaryPurple)};
    }
`;

const InfoRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;

    & > svg {
        flex-shrink: 0;
    }
`;

const InfoText = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
`;

/* Chamada de ação permanente: a pista de clique precisa existir antes do hover,
   porque quem só passa os olhos na listagem nunca chega a passar o mouse. */
const CardFooter = styled.div`
    border-top: 1px solid ${colors.night}1A;
    padding: 4px 8px;
`;

const CtaButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    padding: 8px;
    border: none;
    background: transparent;
    color: ${panelAccent(colors.secondaryPurple)};
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    font-family: inherit;
    border-radius: 6px;
    cursor: pointer;
    text-align: left;
    transition: background 0.15s ease;

    & > svg {
        transition: transform 0.2s ease;
    }

    /* O anel de foco é desenhado pelo card (focus-within), não aqui. */
    &:focus-visible {
        outline: none;
    }

    ${CardContainer}:hover & {
        background: #F3EAFF;
    }

    ${CardContainer}:hover & > svg {
        transform: translateX(3px);
    }

    @media (prefers-reduced-motion: reduce) {
        ${CardContainer}:hover & > svg {
            transform: none;
        }
    }
`;

const DropDownActions = styled.div`
    display: flex;
    flex-direction: column;
    width: max-content;
    min-width: 130px;
    position: absolute;
    border-radius: 10px;
    background: ${colors.neutralLight};
    top: 52px;
    right: 12px;
    padding: 6px;
    border: 1px solid ${colors.night}12;
    box-shadow: 0 8px 24px ${colors.night}1A;
    z-index: 10;
`;

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    padding: 8px 10px;
    border: none;
    background: transparent;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.15s ease;
    font-family: inherit;
    width: 100%;
    white-space: nowrap;

    &:hover {
        background: ${colors.neutralBackground};
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: -2px;
    }

    & + & {
        margin-top: 2px;
    }
`;

const ActionLabel = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${({ color }) => color};
    white-space: nowrap;
`;
